import { useRef, useCallback, useState } from 'react';

interface UndoStackOptions {
  maxHistory?: number;
  debounceMs?: number;
}

export function useUndoStack<T>(options?: UndoStackOptions) {
  const maxHistory = options?.maxHistory ?? 50;
  const debounceMs = options?.debounceMs ?? 500;

  const historyRef = useRef<T[]>([]);
  const indexRef = useRef<number>(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Force re-renders when canUndo/canRedo changes
  const [, setTick] = useState(0);
  const bump = () => setTick(t => t + 1);

  const pushState = useCallback((state: T) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const cloned = structuredClone(state);

      // Truncate any forward history beyond current index
      historyRef.current = historyRef.current.slice(0, indexRef.current + 1);

      // Push the new state
      historyRef.current.push(cloned);

      // Enforce maxHistory
      if (historyRef.current.length > maxHistory) {
        historyRef.current = historyRef.current.slice(historyRef.current.length - maxHistory);
      }

      indexRef.current = historyRef.current.length - 1;
      bump();
    }, debounceMs);
  }, [maxHistory, debounceMs]);

  const undo = useCallback((): T | undefined => {
    if (indexRef.current <= 0) return undefined;
    indexRef.current -= 1;
    bump();
    return structuredClone(historyRef.current[indexRef.current]);
  }, []);

  const redo = useCallback((): T | undefined => {
    if (indexRef.current >= historyRef.current.length - 1) return undefined;
    indexRef.current += 1;
    bump();
    return structuredClone(historyRef.current[indexRef.current]);
  }, []);

  const canUndo = indexRef.current > 0;
  const canRedo = indexRef.current < historyRef.current.length - 1;

  return { pushState, undo, redo, canUndo, canRedo };
}
